/**
 * Dashboard AuthGuard — thin wrapper around the shared ProtectedRoute.
 *
 * The shared ProtectedRoute handles:
 *   - bfcache guard (pageshow reload after idle logout)
 *   - Loading spinner during auth bootstrap
 *   - Redirect to Auth login when unauthenticated
 *
 * This component adds Dashboard-specific logic:
 *   - User status checks (inactive / suspended / pending_verification)
 */

import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ProtectedRoute } from '@airclou/auth-react'
import { useAuth } from '../../contexts/AuthContext'
import { AccessDeniedPage } from './AccessDeniedPage'

interface AuthGuardProps {
  children: ReactNode
}

/**
 * Inner component that runs after ProtectedRoute has confirmed the user is
 * authenticated, so `user` is guaranteed non-null here.
 */
function StatusGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation('dashboard')
  const { user } = useAuth()

  // Check user status for dashboard access
  if (user?.status && user.status !== 'active') {
    console.log('[AuthGuard] User has non-active status:', user.status)

    const getStatusMessage = (status: string): string | undefined => {
      switch (status) {
        case 'inactive':
          return t('authGuard.inactiveMessage')
        case 'suspended':
          return t('authGuard.suspendedMessage')
        case 'pending_verification':
          return t('authGuard.pendingVerificationMessage')
        default:
          return undefined
      }
    }

    return (
      <AccessDeniedPage
        status={user.status as any}
        message={getStatusMessage(user.status)}
      />
    )
  }

  return <>{children}</>
}

export function AuthGuard({ children }: AuthGuardProps) {
  return (
    <ProtectedRoute>
      <StatusGate>{children}</StatusGate>
    </ProtectedRoute>
  )
}
